import axios, { AxiosInstance } from 'axios';
import URL_CONSTANTS from 'utils/constants';
import Cookies from 'js-cookie';
import store from 'utils/redux/store';
import { DataPointTestKitMeasurement } from 'utils/models/test-kit-measurement';
import { globalLogout } from 'index';
import { msalInstance } from '../../index';
import { loginRequest } from '../auth/authConfig';
import { addEventLog } from 'utils/functions/eventLogFunctions';
import { Buffer } from 'buffer';
function decodeToken(token: string) {
    // Split the JWT by its dots and decode the payload (the middle part)
    const payload = JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString('utf-8'));
    return payload;
}
const BASE_URL = process.env.REACT_APP_BACKEND_URI + '/cwds/services/';
const serialNumber = localStorage.getItem('analyzerSerialNumber') ?? 'N/A';
// Define a type for the access token

class ApiService {
	client: AxiosInstance;

	constructor() {
		// Initialize the Axios client
		this.client = axios.create({
			baseURL: BASE_URL,
		});

		// Intercept Axios requests to add the Authorization header
		this.client.interceptors.request.use(
			async (config) => {
				const state = store.getState();
				let accessToken = state.tokens.accessToken;

				if (accessToken) {
					// Check if token is about to expire
					const decodedToken: any = decodeToken(accessToken);
					const currentTime = Date.now() / 1000;
					const timeUntilExpiry = decodedToken.exp - currentTime;
					const threeMinutes = 180; // 3 minutes in seconds
					
					// If token will expire in less than 3 minutes, refresh it
					if (timeUntilExpiry < threeMinutes) {
						try {
							// Get current active account
							const currentAccount = msalInstance.getAllAccounts()[0];
							
							if (currentAccount) {
								const response = await msalInstance.acquireTokenSilent({
									...loginRequest,
									account: currentAccount,
								});

								// Update token in store and cookie
								accessToken = response.accessToken;
								if(accessToken) {
									this.setTokenToCookie(accessToken);
								}
								
								// Dispatch new token to Redux store
								store.dispatch({
									type: 'SET_ACCESS_TOKEN',
									payload: accessToken,
								});
							}
						} catch (error) {
							console.error('Error refreshing token:', error);
							// If silent token acquisition fails, you might want to redirect to login
							// or handle the error differently
						}
					}

					// Set the Authorization header with the current (or refreshed) token
					config.headers['Authorization'] = `Bearer ${accessToken}`;
				}

				return config;
			},
			(error) => {
				return Promise.reject(error);
			}
		);

		this.client.interceptors.response.use(
			(response) => response,
			(error) => {
				// Assuming you have a way to check conditions or directly pass a logout function
				if (
					error.response &&
					(error.response.status === 401 ||
						error.response.status === 400)
				) {
					// Assuming a logout function is available globally or passed in some way
					globalLogout();
				}
				return Promise.reject(error);
			}
		);
	}

	updateAuthorizationHeader(token: string | undefined) {
		if (token) {
			this.client.defaults.headers['Authorization'] = `Bearer ${token}`;
		} else {
			delete this.client.defaults.headers['Authorization'];
		}
	}

	getTokenFromCookie() {
		return Cookies.get('auth_token');
	}

	setTokenToCookie(token: string) {
		Cookies.set('auth_token', token, { expires: 1 });
	}

	// User services
	createUser(data: any) {
		return this.client.post(URL_CONSTANTS.CREATE_USER, data);
	}

	getAuthenticatedUser(userId: string) {
		return this.client.get(
			`${URL_CONSTANTS.GET_AUTHENTICATED_USER}${userId}`
		);
	}

	getSampleGetDataPoints(samplePointId: number) {
		return this.client.get(
			`${URL_CONSTANTS.GET_SAMPLE_POINT}${samplePointId}${URL_CONSTANTS.GET_DATAPOINTS}`
		);
	}

	getLatestDataBySite(id: number) {
		return this.client.get(`${URL_CONSTANTS.GET_LATEST_DATA_BY_SITE}${id}`);
	}

	getSiteListByUserId(userId: number) {
		return this.client.get(
			`${URL_CONSTANTS.GET_SITE_LIST_BY_USERID}${userId}`
		);
	}

	getUser(userEmail: string) {
		return this.client.get(`${URL_CONSTANTS.GET_USER}${userEmail}`);
	}

	checkUserEmail(userEmail: string) {
		return this.client.get(`${URL_CONSTANTS.GET_SIMPLE_USER}${userEmail}`);
	}

	getUserByID(userId: number) {
		return this.client.get(`${URL_CONSTANTS.GET_USER_BY_ID}${userId}`);
	}

	getLocationBySiteID(siteId: number) {
		return this.client.get(
			`${URL_CONSTANTS.GET_SL_LIST_BY_SITEID}${siteId}`
		);
	}

	getTestKitByID(testKitId: number) {
		return this.client.get(`${URL_CONSTANTS.GET_TEST_KIT}${testKitId}`);
	}

	getTestKitByIDAndSamplePointId(testKitId: number, samplePointId: number) {
		return this.client.get(
			`${URL_CONSTANTS.GET_TEST_KIT_SAMPLEPOINT_ID}${testKitId}/${samplePointId}`
		);
	}

	getIndustriesList() {
		return this.client.get(`${URL_CONSTANTS.GET_INDUSTRIES_LIST}`);
	}

	getSiteListPaginated(pageNumber: number, pageSize: number) {
		const requestBody = {
			pageNumber,
			pageSize,
			searchPropertyIds: ['status'],
			searchPropertyValues: ['Active'],
			sortPropertyIds: ['name'],
			sortStates: [false],
		};

		return this.client.post(
			`${URL_CONSTANTS.GET_SITE_LIST_PAGINATED}`,
			requestBody
		);
	}

	postDataPointResult(data: DataPointTestKitMeasurement) {
		return this.client.post(
			`${URL_CONSTANTS.CREATE_DATA_POINT_RESULT}`,
			data
		);
	}

	postUpdateUser(data: any) {
		return this.client.post(`${URL_CONSTANTS.UPDATE_USER}`, data)
		.then((returnData) => {
			if (returnData.status.toString().startsWith("2"))
				addEventLog("Update User Success", "User Profile Management", undefined, undefined);
			else
				addEventLog("Update User Failed", "User Profile Management", undefined, undefined, `Status: ${returnData.statusText}`);
			return returnData;
		})
		.catch((error) => {
			const details = error?.response
				? `Status: ${error.response.status}, Message: ${error.response.data?.message ?? JSON.stringify(error.response.data)}`
				: `Error: ${error.message}`;
			addEventLog("Update User Failed", "User Profile Management", undefined, undefined, `${details}`);
			return Promise.reject(error);
		});
	}

	postDownloadReportCSV(data: any) {
		return this.client.post(`${URL_CONSTANTS.DOWNLOAD_REPORT_CSV}`, data);
	}

	getBackendVersion() {
		return this.client.get(URL_CONSTANTS.GET_BACKEND_VERSION);
	}

	getUserGroups(userId: number) {
		return this.client.get(`${URL_CONSTANTS.GET_USER_GROUPS}${userId}`);
	}

	createAGroup(data: any, creatorEmail: any, skipEventLog?: boolean) {
		if (!data?.name){
			if (!skipEventLog)
            	addEventLog("Create Group Failed", "Group Management", undefined, undefined, `Group Name: N/A, Creator: ${creatorEmail}, Error: No Group name provided.`);
        	return Promise.reject(new Error("Group name is required"));
		}
		return this.client.post(`${URL_CONSTANTS.CREATE_A_GROUP}`, data)
		.then((returnData) => {
			if (skipEventLog) return returnData;
			if (returnData.status.toString().startsWith("2"))
				addEventLog("Create Group Success", "Group Management", undefined, undefined, `Group Name: ${data.name}, Creator: ${creatorEmail}`);
			else
				addEventLog("Create Group Failed", "Group Management", undefined, undefined, `Group Name: ${data.name}, Creator: ${creatorEmail}, Status: ${returnData.statusText}`);
			return returnData;
		})
		.catch((error) => {
			const details = error?.response
				? `Status: ${error.response.status}, Message: ${error.response.data?.message ?? JSON.stringify(error.response.data)}`
				: `Error: ${error.message}`;
			if (!skipEventLog)
				addEventLog("Create Group Failed", "Group Management", undefined, undefined, `Group Name: ${data.name}, Creator: ${creatorEmail} ${details}`);
			return Promise.reject(error);
		});
	}

	deleteAGroup(groupId: number, userId?: number, skipEventLog?: boolean) {
		return this.client.delete(`${URL_CONSTANTS.DELETE_A_GROUP}/${groupId}`)
		.then((returnData) => {
			if (skipEventLog) return returnData;
			if (returnData.status.toString().startsWith("2"))
				addEventLog("Delete Group Success", "Group Management", undefined, undefined, `GroupId: ${groupId}, UserId: ${userId || 'UNKNOWN'}`);
			else
				addEventLog("Delete Group Failed", "Group Management", undefined, undefined, `GroupId: ${groupId}, UserId: ${userId || 'UNKNOWN'}, Status: ${returnData.statusText}`);
			return returnData;
		})
		.catch((error) => {
			const details = error?.response
				? `Status: ${error.response.status}, Message: ${error.response.data?.message ?? JSON.stringify(error.response.data)}`
				: `Error: ${error.message}`;
			if (!skipEventLog)
				addEventLog("Delete Group Failed", "Group Management", undefined, undefined, `GroupId: ${groupId}, UserId: ${userId || 'UNKNOWN'}, ${details}`);
			return Promise.reject(error);
		});
	}

	getASite(siteId: number) {
		return this.client.get(`${URL_CONSTANTS.GET_A_SITE}${siteId}`);
	}

	createNewSite(data: any, orgId: number, creatorEmail: string, skipEventLog?: boolean) {
		if (!data?.name){
			if (!skipEventLog)
            	addEventLog("Create Site Failed", "Site Management", undefined, undefined, `Site Name: N/A, Creator: ${creatorEmail}, Error: No Site name provided.`);
        	return Promise.reject(new Error("Site name is required"));
		}
		return this.client.post(`${URL_CONSTANTS.CREATE_NEW_SITE}/${orgId}/sites/`, data)
        .then((returnData) => {
            if (skipEventLog) return returnData;
            if (returnData.status.toString().startsWith("2"))
                addEventLog("Create Site Success", "Site Management", undefined, undefined, `Site Name: ${data.name}, Creator: ${creatorEmail}`);
			else
				addEventLog("Create Site Failed", "Site Management", undefined, undefined, `Site Name: ${data.name}, Creator: ${creatorEmail}, Status: ${returnData.statusText}`);
            return returnData;
        })
        .catch((error) => {
			const details = error?.response
				? `Status: ${error.response.status}, Message: ${error.response.data?.message ?? JSON.stringify(error.response.data)}`
				: `Error: ${error.message}`;
            if (!skipEventLog)
                addEventLog("Create Site Failed", "Site Management", undefined, undefined, `Site Name: ${data.name}, Creator: ${creatorEmail}, ${details}`);
            return Promise.reject(error);
        });
	}

	editASite(data: any, siteId: number, skipEventLog?: boolean) {
		if (!data?.name){
			if (!skipEventLog)
            	addEventLog("Edit Site Failed", "Site Management", undefined, undefined, `Site Name: N/A, Error: No Site name provided.`);
        	return Promise.reject(new Error("Site name is required"));
		}
		return this.client.put(`${URL_CONSTANTS.EDIT_A_SITE}${siteId}`, data)
        .then((returnData) => {
            if (skipEventLog) return returnData;
            if (returnData.status.toString().startsWith("2"))
                addEventLog("Edit Site Success", "Site Management", siteId, undefined);
			else
				addEventLog("Edit Site Failed", "Site Management", siteId, undefined, `Status: ${returnData.statusText}`);
            return returnData;
        })
        .catch((error) => {
			const details = error?.response
				? `Status: ${error.response.status}, Message: ${error.response.data?.message ?? JSON.stringify(error.response.data)}`
				: `Error: ${error.message}`;
            if (!skipEventLog)
                addEventLog("Edit Site Failed", "Site Management", siteId, undefined, `${details}`);
            return Promise.reject(error);
        });
	}

	deleteASite(siteId: number, skipEventLog?: boolean) {
		return this.client.put(`${URL_CONSTANTS.DELETE_A_SITE}${siteId}`)
		.then((returnData) => {
			if (skipEventLog) return returnData;
			if (returnData.status.toString().startsWith("2"))
				addEventLog("Delete Site Success", "Site Management", siteId, undefined);
			else
				addEventLog("Delete Site Failed", "Site Management", siteId, undefined, `Status: ${returnData.statusText}`);
			return returnData;
		})
		.catch((error) => {
			const details = error?.response
				? `Status: ${error.response.status}, Message: ${error.response.data?.message ?? JSON.stringify(error.response.data)}`
				: `Error: ${error.message}`;
			if (!skipEventLog)
				addEventLog("Delete Site Failed", "Site Management", siteId, undefined, `${details}`);
			return Promise.reject(error);
		});
	}

	moveSite = (groupId: number, siteId: number) => {
		return this.client.put(
			`${URL_CONSTANTS.MOVE_SITE}${groupId}/${siteId}`
		);
	};

	getSiteUsers = (siteId: number) => {
		return this.client.get(`${URL_CONSTANTS.GET_SITE_USERS}${siteId}`);
	};
	
	addSiteOwner = (data: any, siteId: number, user: any, skipEventLog?: boolean) => {
		return this.client.post(`${URL_CONSTANTS.ADD_SITE_OWNER}`, data)
		.then((returnData) => {
			if (skipEventLog) return returnData;
			if (returnData.status.toString().startsWith("2"))
				addEventLog("Add Site Owner Success", "Site Management", siteId, undefined, `Added UserID: ${user.id}`);
			else
				addEventLog("Add Site Owner Failed", "Site Management", siteId, undefined, `Added UserID: ${user.id}, Status: ${returnData.statusText}`);
			return returnData;
		})
		.catch((error) => {
			const details = error?.response
				? `Status: ${error.response.status}, Message: ${error.response.data?.message ?? JSON.stringify(error.response.data)}`
				: `Error: ${error.message}`;
			if (!skipEventLog)
				addEventLog("Add Site Owner Failed", "Site Management", siteId, undefined, `Added UserID: ${user.id}, ${details}`);
			return Promise.reject(error);
		});
	};

	removeSiteOwner = (data: any, siteId: number, user: any, skipEventLog?: boolean) => {
		return this.client.post(`${URL_CONSTANTS.REMOVE_SITE_OWNER}`, data)
		.then((returnData) => {
			if (skipEventLog) return returnData;
			if (returnData.status.toString().startsWith("2"))
				addEventLog("Remove Site Owner Success", "Site Management", siteId, undefined, `Removed UserID: ${user.id}`);
			else
				addEventLog("Remove Site Owner Failed", "Site Management", siteId, undefined, `Removed UserID: ${user.id}, Status: ${returnData.statusText}`);
			return returnData;
		})
		.catch((error) => {
			const details = error?.response
				? `Status: ${error.response.status}, Message: ${error.response.data?.message ?? JSON.stringify(error.response.data)}`
				: `Error: ${error.message}`;
			if (!skipEventLog)
				addEventLog("Remove Site Owner Failed", "Site Management", siteId, undefined, `Removed UserID: ${user.id}, ${details}`);
			return Promise.reject(error);
		});
	};

	shareSite = (data: any, newInviteEmail: string, siteId: number, skipEventLog?: boolean) => {
		return this.client.post(`${URL_CONSTANTS.SHARE_SITE}`, data)
		.then((returnData) => {
			if (skipEventLog) return returnData;
			if (returnData.status.toString().startsWith("2"))
				addEventLog("Share Site Success", "Site Management", siteId, undefined, `Email Shared To: ${newInviteEmail}`);
			else
				addEventLog("Share Site Failed", "Site Management", siteId, undefined, `Email Shared To: ${newInviteEmail}, Status: ${returnData.statusText}`);
			return returnData;
		})
		.catch((error) => {
			const details = error?.response
				? `Status: ${error.response.status}, Message: ${error.response.data?.message ?? JSON.stringify(error.response.data)}`
				: `Error: ${error.message}`;
			if (!skipEventLog)
				addEventLog("Share Site Failed", "Site Management", siteId, undefined, `Email Shared To: ${newInviteEmail}, ${details}`);
			return Promise.reject(error);
		});
	};

	getTestKits = (orgId: number) => {
		return this.client.get(
			`${URL_CONSTANTS.GET_TESTKITS_BY_ORG_ID}?orgId=${orgId}`
		);
	};

	addNewLocation = (data: any, skipEventLog?: boolean) => {
		return this.client.post(`${URL_CONSTANTS.ADD_NEW_LOCATION}`, data)
		.then((returnData) => {
			if (skipEventLog) return returnData;
			if (returnData.status.toString().startsWith("2"))
				addEventLog("Add New Location Success", "Location Management", data.siteID, undefined, `New Location: ${data.locationName}`);
			else
				addEventLog("Add New Location Failed", "Location Management", data.siteID, undefined, `New Location: ${data.locationName}, Status: ${returnData.statusText}`);
			return returnData;
		})
		.catch((error) => {
			const details = error?.response
				? `Status: ${error.response.status}, Message: ${error.response.data?.message ?? JSON.stringify(error.response.data)}`
				: `Error: ${error.message}`;
			if (!skipEventLog)
				addEventLog("Add New Location Failed", "Location Management", data.siteID, undefined, `New Location: ${data.locationName}, ${details}`);
			return Promise.reject(error);
		});
	};

	editLocation = (data: any, action: string, skipEventLog?: boolean) => {
		return this.client.put(`${URL_CONSTANTS.EDIT_LOCATION}`, data)
		.then((returnData) => {
			if (skipEventLog) return returnData;
			if (returnData.status.toString().startsWith("2"))
				addEventLog(`${action === 'edit' ? 'Edit' : 'Delete'} Location Success`, "Location Management", data.siteID, undefined, `Location: ${data.locationName}`);
			else
				addEventLog(`${action === 'edit' ? 'Edit' : 'Delete'} Location Failed`, "Location Management", data.siteID, undefined, `Location ${data.locationName}, Status: ${returnData.statusText}`);
			return returnData;
		})
		.catch((error) => {
			const details = error?.response
				? `Status: ${error.response.status}, Message: ${error.response.data?.message ?? JSON.stringify(error.response.data)}`
				: `Error: ${error.message}`;
			if (!skipEventLog)
				addEventLog(`${action === 'edit' ? 'Edit' : 'Delete'} Location Failed`, "Location Management", data.siteID, undefined, `Location ${data.locationName}, ${details}`);
			return Promise.reject(error);
		}); 
	};

	updateAlarmSettings(data: any, siteId: number, comments: string, skipEventLog?: boolean) {
		return this.client.post(`${URL_CONSTANTS.UPDATE_ALARMS}`, data)
		.then((returnData) => {
			if (skipEventLog) return returnData;
			if (returnData.status.toString().startsWith("2"))
				addEventLog("Alarm Settings Update Success", "Alarm Settings", siteId, undefined, comments);
			else
				addEventLog("Alarm Settings Update Failed", "Alarm Settings", siteId, undefined, `${comments}, Status: ${returnData.statusText}`);
			return returnData;
		})
		.catch((error) => {
			const details = error?.response
				? `Status: ${error.response.status}, Message: ${error.response.data?.message ?? JSON.stringify(error.response.data)}`
				: `Error: ${error.message}`;
			if (!skipEventLog)
				addEventLog("Alarm Settings Update Failed", "Alarm Settings", siteId, undefined, `${comments} ${details}`);
			return Promise.reject(error);
		});
	}

	syncServiceFlag = (state: number) => {
		return this.client.put(`${URL_CONSTANTS.SYNC_SERVICE_FLAG}${state}`);
	};

	getTableColumns = (siteId: number) => {
		return this.client.get(`${URL_CONSTANTS.GET_TABLE_COLUMNS}${siteId}`);
	};

	postCreateUpdateTableColumns(data: any, siteId: number, columnDiffs: string, skipEventLog?: boolean) {
		return this.client.post(`${URL_CONSTANTS.POST_TABLE_COLUMNS}`, data)
		.then((returnData) => {
			if (skipEventLog) return returnData;
			if (returnData.status.toString().startsWith("2"))
				addEventLog("Table Columns Update Success", "Table Columns", siteId, undefined, columnDiffs);
			else
				addEventLog("Table Columns Update Failed", "Table Columns", siteId, undefined, `${columnDiffs}, Status: ${returnData.statusText}`);
			return returnData;
		})
		.catch((error) => {
			const details = error?.response
				? `Status: ${error.response.status}, Message: ${error.response.data?.message ?? JSON.stringify(error.response.data)}`
				: `Error: ${error.message}`;
			if (!skipEventLog)
				addEventLog("Table Columns Update Failed", "Table Columns", siteId, undefined, `${columnDiffs} ${details}`);
			return Promise.reject(error);
		});
	}

	getAllData = (siteId: number) => {
		return this.client.get(
			`${URL_CONSTANTS.GET_ALL_DATA_BY_SITE}${siteId}`
		);
	};

	getExportSettings = () => {
		return this.client.get(
			`${URL_CONSTANTS.GET_EXPORT_SETTINGS}`
		);
	};

	saveExportSettings = (data: any) => {
		return this.client.post(`${URL_CONSTANTS.SAVE_EXPORT_SETTINGS}`, data);
	};

	getDataPoint = (id: number) => {
		return this.client.get(`${URL_CONSTANTS.GET_DATA_POINT_RESULTS}${id}`);
	};

	postSortOrderSitesAndGroups = (data: any) => {
		return this.client.post(
			`${URL_CONSTANTS.SORT_ORDERS_SITES_GROUPS}`,
			data
		);
	};

	editGroup(data: any, skipEventLog?: boolean) {
		if (!data?.name){
			if (!skipEventLog)
            	addEventLog("Edit Group Failed", "Group Management", undefined, undefined, `Group Name: N/A, Error: No Group name provided.`);
        	return Promise.reject(new Error("Group name is required"));
		}
		return this.client.post(`${URL_CONSTANTS.EDIT_A_GROUP}`, data)
		.then((returnData) => {
			if (skipEventLog) return returnData;
			if (returnData.status.toString().startsWith("2"))
				addEventLog("Edit Group Success", "Group Management", undefined, undefined, `Group Id: ${data.id}, Name: ${data.name}`);
			else
				addEventLog("Edit Group Failed", "Group Management", undefined, undefined, `Group Id: ${data.id}, Name: ${data.name}, Status: ${returnData.statusText}`);
			return returnData;
		})
		.catch((error) => {
			const details = error?.response
				? `Status: ${error.response.status}, Message: ${error.response.data?.message ?? JSON.stringify(error.response.data)}`
				: `Error: ${error.message}`;
			if (!skipEventLog)
				addEventLog("Edit Group Failed", "Group Management", undefined, undefined, `Group Id: ${data.id}, Name: ${data.name}, ${details}`);
			return Promise.reject(error);
		});
	}

	deleteAccount(data: any, skipEventLog?: boolean) {
		return this.client.post(`${URL_CONSTANTS.DELETE_USER}`, data)
		.then((returnData) => {
			if (skipEventLog) return returnData;
			if (returnData.status.toString().startsWith("2"))
				addEventLog("Delete Account Success", "User Profile Management", undefined, undefined, `Email: ${data.email}`);
			else
				addEventLog("Delete Account Failed", "User Profile Management", undefined, undefined, `Email: ${data.email}, Status: ${returnData.statusText}`);
			return returnData;
		})
		.catch((error) => {
			const details = error?.response
				? `Status: ${error.response.status}, Message: ${error.response.data?.message ?? JSON.stringify(error.response.data)}`
				: `Error: ${error.message}`;
			if (!skipEventLog)
				addEventLog("Delete Account Failed", "User Profile Management", undefined, undefined, `Email: ${data.email}, ${details}`);
			return Promise.reject(error);
		});
	}

  getAllDataNew = (id: number) => {
		return this.client.get(`${URL_CONSTANTS.GET_ALL_DATA_NEW}${id}`);
	};
	callFailsApis = (url: string,data: any, method: "get" | "post" | "put" | "patch" | "delete") => {
		return this.client[method](`${url}`,data);
	};

	getAllUsersOfOrg = (orgId: number, data: any) => {
		return this.client.post(`${URL_CONSTANTS.GET_ALL_USERS_LIST_OF_ORG}${orgId}`, data);
	};

	addEventLog = (data: any) => {
		return this.client.post(`${URL_CONSTANTS.ADD_EVENT_LOG_ENTRY}`, data);	
	};

	addEventLogAnonymous = (data: any) => {
		return this.client.post(`${URL_CONSTANTS.ADD_EVENT_LOG_ENTRY_ANONYMOUS}`, data);
	};

	uploadSyncData = (data: any, skipEventLog?: boolean) => {
		return this.client.post(`${URL_CONSTANTS.UPLOAD_SYNC_DATA}`, data)
		.then((returnData) => {
			if (skipEventLog) return returnData;
			if (returnData.status.toString().startsWith("2"))
				addEventLog("Upload Sync Data Success", "Sync", undefined, undefined);
			else
				addEventLog("Upload Sync Data Failed", "Sync", undefined, undefined, `Status: ${returnData.statusText}`);
			return returnData;
		})
		.catch((error) => {
			const details = error?.response
				? `Status: ${error.response.status}, Message: ${error.response.data?.message ?? JSON.stringify(error.response.data)}`
				: `Error: ${error.message}`;
			if (!skipEventLog)
				addEventLog("Upload Sync Data Failed", "Sync", undefined, undefined, `${details}`);
			return Promise.reject(error);
		});
	};

	setAnalyzerWarranty = (data: any, skipEventLog?: boolean) => {
		return this.client.post(`${URL_CONSTANTS.SET_ANALYZER_WARRANTY}`, data)
		.then((returnData) => {
			if (skipEventLog) return returnData;
			if (returnData?.data === 'Error')
				addEventLog(`Warranty Registration failed`, "Warranty Registration", undefined, undefined, `Serial: ${serialNumber} already registered`);
			else if (returnData?.data === "success")
				addEventLog(`Warranty Registration successful`, "Warranty Registration", undefined, undefined, `Serial: ${serialNumber}`);
			else
				addEventLog(`Warranty Registration failed`, "Warranty Registration", undefined, undefined, `Serial: ${serialNumber}, unexpected response: ${returnData?.data}`);
			return returnData;
		})
		.catch((error) => {
			const details = error?.response
				? `Status: ${error.response.status}, Message: ${error.response.data?.message ?? JSON.stringify(error.response.data)}`
				: `Error: ${error.message}`;
			if (!skipEventLog)
				addEventLog(`Warranty Registration failed`, "Warranty Registration", undefined, undefined, `Serial: ${serialNumber}, ${details}`);
			return Promise.reject(error);
		});
	};

	// CoC Lab Services
	getCocLabs = () => {
		return this.client.get(URL_CONSTANTS.GET_COC_LABS);
	};

	getCocPresets = (labId: number) => {
		return this.client.get(`${URL_CONSTANTS.GET_COC_PRESETS}${labId}`);
	};

	getCocGroups = (presetId: number) => {
		return this.client.get(`${URL_CONSTANTS.GET_COC_GROUPS}${presetId}`);
	};

	getCocPanelAnalyses = (groupId: number) => {
		return this.client.get(`${URL_CONSTANTS.GET_COC_PANEL_ANALYSES}${groupId}`);
	};

	getAnalyzerWarranties = (userId: number) => {
		return this.client.get(`${URL_CONSTANTS.GET_ANALYZER_WARRANTIES}${userId}`)
	}
	getCocForms = () => {
		return this.client.get(`${URL_CONSTANTS.GET_COC_FORMS}`);
	}
	createCocLabRequest = (data: any) => {
		return this.client.post(URL_CONSTANTS.CREATE_COC_LAB_REQUEST, data);
	};
	getCocSites = () => {
		return this.client.get(`${URL_CONSTANTS.GET_COC_SITES}`);
	}

	getLatestAppVersionBCA = (orgId: number) => {
		return this.client.get(`${URL_CONSTANTS.GET_LATEST_BCA_APP_VERSION}${orgId}`);
	}
	// ... and so on
}

export default new ApiService();
